import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { DatePicker, message, Tooltip, Input } from "antd";
import moment from "moment";
import DataParser from "../../../utlits/Dataparser";
import WfhService from "../../../Services/WfhService";
import LeavetypeService from "../../../Services/PolicyServices";

const { TextArea } = Input;

const AddWfh = (props) => {
    const { id, labelledby, refreshData } = props;

    const [reason, setReason] = useState("");
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [numberOfDays, setNumberOfDays] = useState(0);
    const [wfhDates, setWfhDates] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userPolicy, setUserPolicy] = useState(null);

    const companyId = DataParser.CompanyId();
    const userId = DataParser.UserId();

    // 🟢 Fetch policy to get holidays/off-days
    useEffect(() => {
        if (companyId && userId) {
            LeavetypeService.getUserLeavePolicy(companyId, userId)
                .then((res) => {
                    const policy = Array.isArray(res.data) ? res.data[0] : (res.data?.data?.[0] || null);
                    setUserPolicy(policy);
                })
                .catch((err) => console.error("Error fetching WFH policy:", err));
        }
    }, [companyId, userId]);

    // 🟢 Helper: Alternate Off Day Logic
    const isAlternateOff = (policyDay, dateInput) => {
        if (!policyDay.alternate || !policyDay.alternate_type) return false;
        const date = moment(dateInput).startOf('day');
        const baseDate = moment(policyDay.alternate, ["YYYY-MM-DD", "DD-MM-YYYY", "MM-DD-YYYY"]).startOf('day');
        if (!date.isValid() || !baseDate.isValid() || date.isBefore(baseDate)) return false;
        if (date.format('dddd').toLowerCase() !== baseDate.format('dddd').toLowerCase()) return false;

        const diffWeeks = Math.floor(date.diff(baseDate, 'days') / 7);
        const cycleMap = { "alternate": 2, "bialternate": 3, "trialternate": 4 };
        const cycle = cycleMap[policyDay.alternate_type?.toLowerCase()] || 2;
        const status = policyDay.status || 'off';

        return status === 'off' ? (diffWeeks % cycle === 0) : (diffWeeks % cycle !== 0);
    };

    // 🟢 Helper: Check if date is OFF or Holiday
    const isOffOrHoliday = (mDate) => {
        if (!userPolicy) return false;
        const d = mDate.format("YYYY-MM-DD");
        const holidays = userPolicy.holidays || [];
        if (holidays.includes(d)) return true;

        if (userPolicy.attendance_days && Array.isArray(userPolicy.attendance_days)) {
            const dayName = mDate.format("dddd").toLowerCase();
            const policyDay = userPolicy.attendance_days.find(p => p.day.toLowerCase() === dayName);
            if (policyDay) {
                if (!policyDay.active) return true;
                if (isAlternateOff(policyDay, mDate)) return true;
            }
        }

        const weekOffs = (userPolicy.off_days || [0, 6]).map(day => Number(day) === 7 ? 0 : Number(day));
        return weekOffs.includes(mDate.day());
    };

    // ✅ Calculate WFH dates and count
    useEffect(() => {
        if (!fromDate || !toDate) {
            setWfhDates([]);
            setNumberOfDays(0);
            return;
        }

        const start = moment(fromDate).startOf("day");
        const end = moment(toDate).startOf("day");

        if (end.isBefore(start)) {
            setWfhDates([]);
            setNumberOfDays(0);
            return;
        }

        const dates = [];
        let cur = start.clone();
        while (!cur.isAfter(end)) {
            if (!isOffOrHoliday(cur)) {
                dates.push(cur.format("YYYY-MM-DD"));
            }
            cur.add(1, "day");
        }

        setWfhDates(dates);
        setNumberOfDays(dates.length);
    }, [fromDate, toDate, userPolicy]);

    const disabledDate = (current) => {
        // Disable past dates
        if (current && current < moment().startOf('day')) return true;

        // Disable holidays/off-days
        return isOffOrHoliday(current);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!fromDate || !toDate) {
            message.error("Please select both Start and End dates.");
            return;
        }

        if (!reason.trim()) {
            message.error("Please provide a reason for WFH.");
            return;
        }

        setIsSubmitting(true);

        const body = {
            company_id: companyId,
            empid: userId,
            from_date: moment(fromDate).format("YYYY-MM-DD"),
            to_date: moment(toDate).format("YYYY-MM-DD"),
            reason: reason,
            wfh_dates: wfhDates
        };

        try {
            const res = await WfhService.create(body);
            if (res.Good) {
                message.success("WFH Application submitted successfully!");
                setReason("");
                setFromDate(null);
                setToDate(null);
                document.getElementById("closeWfh").click();
                if (refreshData) refreshData();
            } else {
                message.error(res.data || "Failed to submit application.");
            }
        } catch (err) {
            console.error(err);
            message.error("Server error. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="modal fade"
            id={id}
            tabIndex="-1"
            aria-labelledby={labelledby}
            aria-hidden="true"
            data-bs-keyboard="false"
            style={{ zIndex: 1060 }}
        >
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
                    <form onSubmit={handleSubmit}>
                        {/* Header */}
                        <div className="modal-header border-0 pb-1">
                            <h5 className="modal-title fw-bold" id={labelledby} style={{ color: "#1D1D1D" }}>
                                Apply for Work From Home
                            </h5>
                            <button
                                type="button"
                                className="btn-close shadow-none"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                id="closeWfh"
                                onClick={() => {
                                    setReason("");
                                    setFromDate(null);
                                    setToDate(null);
                                }}
                            />
                        </div>

                        {/* Body */}
                        <div className="modal-body py-2">
                            <div className="row g-3">
                                {/* Start Date */}
                                <div className="col-md-6">
                                    <div className={`float-select-wrap ${fromDate ? "has-value" : ""}`}>
                                        <label>
                                            Start Date*
                                            <Tooltip title="Select the date from where you want to work from home." placement="top">
                                                <i className="fa fa-info-circle float-label-helper-icon" />
                                            </Tooltip>
                                        </label>
                                        <DatePicker
                                            value={fromDate}
                                            onChange={setFromDate}
                                            format="DD-MM-YYYY"
                                            disabledDate={disabledDate}
                                            placeholder=""
                                            className="w-100"
                                        />
                                    </div>
                                </div>

                                {/* End Date */}
                                <div className="col-md-6">
                                    <div className={`float-select-wrap ${toDate ? "has-value" : ""}`}>
                                        <label>
                                            End Date*
                                            <Tooltip title="Select the date till when your work from home will end." placement="top">
                                                <i className="fa fa-info-circle float-label-helper-icon" />
                                            </Tooltip>
                                        </label>
                                        <DatePicker
                                            value={toDate}
                                            onChange={setToDate}
                                            format="DD-MM-YYYY"
                                            disabled={!fromDate}
                                            disabledDate={(current) => {
                                                if (!fromDate) return false;
                                                const isBeforeFrom = current && current < moment(fromDate).startOf('day');
                                                return isBeforeFrom || disabledDate(current);
                                            }}
                                            placeholder=""
                                            className="w-100"
                                        />
                                    </div>
                                </div>

                                {/* Total Days Display */}
                                {numberOfDays > 0 && (
                                    <div className="col-12">
                                        <div className="p-2 rounded border d-flex justify-content-between align-items-center" style={{ backgroundColor: "#f8f9fa" }}>
                                            <span className="text-secondary small fw-medium">Total Days Calculated:</span>
                                            <span className="badge bg-primary px-3 py-2" style={{ borderRadius: "5px", fontSize: "0.85rem" }}>
                                                {numberOfDays} {numberOfDays === 1 ? "Day" : "Days"}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Reason */}
                                <div className="col-12 mb-1">
                                    <div className={`float-input-wrap ${reason ? "has-value" : ""}`}>
                                        <TextArea
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                            placeholder=" "
                                            autoSize={{ minRows: 2, maxRows: 6 }}
                                            style={{}}
                                        />
                                        <label>
                                            WFH Reason*
                                            <Tooltip title="Explain why you need to work from home to help manage and approve your request." placement="top">
                                                <i className="fa fa-info-circle float-label-helper-icon" />
                                            </Tooltip>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="modal-footer border-0 pt-0 justify-content-end">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="btn btn-primary waves-effect waves-light px-4"
                                style={{ borderRadius: "8px", height: "40px", fontWeight: "600" }}
                            >
                                {isSubmitting ? "Applying..." : "Apply Now"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddWfh;
